import { Input } from '@frontend/shadcn/components/ui/input';
import { HandWaveIcon, PlusIcon, MinusIcon, CircleInfoIcon } from '@revfluence/fresh-icons/solid/esm';
import React from 'react';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@frontend/shadcn/components/ui/accordion';
import { ImagePreview, VideoPreview, RichTextEditor, isVideoUrl } from '@frontend/app/refresh-components';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Switch } from '@revfluence/fresh';
import { useRecruitment, useAmplifyHomePage } from '../RecruitmentContext';

export const AmplifyHeroSection = () => {
  const {
    addAmplifyHeroContentCarousel,
    removeAmplifyHeroContentCarousel,
    setAmplifyHeroTitle,
    setAmplifyHeroDescription,
    setAmplifyHeroHasDescription,
    setAmplifyHeroPrimaryButtonLabel,
    setAmplifyHeroShowSection,
  } = useRecruitment();

  const homePage = useAmplifyHomePage();

  if (!homePage) {
    return null;
  }

  const {
    hero,
  } = homePage;

  const {
    carouselImages,
    title,
    description,
    hasDescription,
    primaryButtonLabel,
    showSection,
  } = hero;

  const handleAddAmplifyHeroContentCarousel = (content: string) => {
    addAmplifyHeroContentCarousel(content);
  };

  const handleRemoveAmplifyHeroContentCarousel = (index: number) => {
    removeAmplifyHeroContentCarousel(index);
  };

  const handleMediaSelect = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,video/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          handleAddAmplifyHeroContentCarousel(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  const renderMediaPreview = (content: string, index: number) => {
    if (isVideoUrl(content)) {
      return (
        <VideoPreview
          key={index}
          videoUrl={content}
          onRemove={() => handleRemoveAmplifyHeroContentCarousel(index)}
        />
      );
    }
    // fallback: treat as image if not video
    return (
      <ImagePreview
        key={index}
        imageUrl={content}
        onRemove={() => handleRemoveAmplifyHeroContentCarousel(index)}
      />
    );
  };

  return (
    <AccordionItem value="hero" className="border-b-0">
      <AccordionTrigger>
        <div className="flex items-center gap-2 w-full">
          <div className="w-6 h-6 bg-green-5 rounded-lg text-primary-foreground flex items-center justify-center">
            <HandWaveIcon fontSize={12} />
          </div>
          <span className="text-base font-medium">Hero</span>
          <div className="ml-auto mr-2 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <Switch
              checked={showSection}
              onChange={(checked) => setAmplifyHeroShowSection(checked)}
              size="small"
            />
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        {/* Content Carousel Section */}
        <div className="flex flex-col gap-2 items-start">
          <div className="flex items-center gap-1">
            <label className="text-xs font-medium">Content Carousel <span className="text-primaryMuted font-normal">(Required)</span></label>
            <CircleInfoIcon fontSize={12} className="text-grey-4" />
          </div>
          <div className="flex gap-3 flex-wrap">
            {carouselImages.map((content, index) => renderMediaPreview(content, index))}
          </div>
          <Button
            variant="link"
            size="xs"
            className="gap-2 pl-0"
            onClick={handleMediaSelect}
          >
            <PlusIcon />
            Add Image or Video
          </Button>
        </div>

        {/* Title Section */}
        <div className="flex flex-col gap-2 mt-4">
          <label htmlFor="amplifyHeroTitle" className="text-xs font-medium text-foreground">Title <span className="text-primaryMuted font-normal">(Required)</span></label>
          <div className="px-[1px]">
            <Input id="amplifyHeroTitle" placeholder="Join Our Affiliate Program!" value={title} onChange={(e) => setAmplifyHeroTitle(e.target.value)} />
          </div>
        </div>

        {/* Description Section */}
        <div className="mt-2">
          {hasDescription && (
          <div className="flex flex-col items-start gap-2">
            <Button
              variant="link"
              className="gap-2 pl-0"
              onClick={() => setAmplifyHeroHasDescription(false)}
              size="xs"
            >
              <MinusIcon />
              Remove Description
            </Button>
            <RichTextEditor
              placeholder="Welcome to your page! Write something amazing about your brand and give creators more information about this collaboration."
              value={description}
              onChange={setAmplifyHeroDescription}
              className="[&_.ql-editor]:min-h-[100px]"
            />
          </div>
            )}
          {!hasDescription && (
          <Button
            variant="link"
            className="gap-2 pl-0"
            onClick={() => setAmplifyHeroHasDescription(true)}
            size="xs"
          >
            <PlusIcon />
            Add Description
          </Button>
            )}
        </div>

        {/* Primary Button Label Section */}
        <div className="mt-4">
          <label htmlFor="buttonLabel" className="text-xs font-medium text-foreground">Primary Button Label <span className="text-primaryMuted">(Required)</span></label>
          <div className="px-[1px] pb-[1px]">
            <Input id="buttonLabel" placeholder="Get Started" className="mt-2" value={primaryButtonLabel} onChange={(e) => setAmplifyHeroPrimaryButtonLabel(e.target.value)} />
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
